import React, { useState, useEffect, useCallback } from 'react';
import { getSouscriptionsStatistiques } from '../../services/souscriptionService';
import { useSouscriptionUpdates } from '../../hooks/useWebSocket';

const StatistiquesSection = () => {
  const [stats, setStats] = useState({
    total: 0,
    attente_paiement: 0,
    attente_livraison: 0,
    livre: 0,
    cloture: 0,
    supprimee: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStatistiques = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getSouscriptionsStatistiques();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatistiques();
  }, [loadStatistiques]);

  // Écouter les mises à jour WebSocket des souscriptions
  useSouscriptionUpdates(useCallback(() => {
    console.log('🔔 Mise à jour des statistiques suite à changement souscription');
    loadStatistiques();
  }, [loadStatistiques]));

  const StatCard = ({ title, value, icon, color, bgColor, percentage }) => (
    <div className={`${bgColor} rounded-2xl p-4 sm:p-6 border-l-4 ${color} shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden`}>
      <div className="flex flex-col h-full">
        {/* Header avec icon et titre */}
        <div className="flex items-start space-x-2 sm:space-x-3 mb-3">
          <div className={`text-2xl sm:text-3xl flex-shrink-0 ${color.replace('border-', 'text-')}`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide overflow-hidden"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.4em',
                maxHeight: '2.8em'
              }}
            >
              {title}
            </p>
            <p className={`text-2xl sm:text-3xl font-bold ${color.replace('border-', 'text-')} truncate`}>
              {loading ? '...' : value}
            </p>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="mt-auto">
          <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
            <span className="truncate flex-shrink mr-2">% du total</span>
            <span className="font-semibold flex-shrink-0">{loading ? '...' : percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-2 rounded-full ${color.replace('border-', 'bg-')} transition-all duration-700 ease-out`}
              style={{ width: loading ? '0%' : `${percentage}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );

  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  if (error) {
    return (
      <div className="mb-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 14.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="text-lg font-medium text-red-900">Erreur de chargement</h3>
              <p className="text-red-700">{error}</p>
            </div>
            <button 
              onClick={loadStatistiques}
              className="ml-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 sm:mb-8">
      {/* Header de la section */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-orange-600 bg-clip-text text-transparent truncate">
                Tableau de Bord - Statistiques
              </h2>
              <p className="text-sm sm:text-base text-gray-600 truncate">Vue d'ensemble des souscriptions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Grid des statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 sm:gap-6">
        {/* Total des souscriptions */}
        <StatCard
          title="Total Souscriptions"
          value={stats.total}
          icon="📊"
          color="border-blue-500 text-blue-600"
          bgColor="bg-gradient-to-br from-blue-50 to-blue-100"
          percentage={100}
        />

        {/* En attente de paiement */}
        <StatCard
          title="Attente Paiement"
          value={stats.attente_paiement}
          icon="💳"
          color="border-yellow-500 text-yellow-600"
          bgColor="bg-gradient-to-br from-yellow-50 to-yellow-100"
          percentage={calculatePercentage(stats.attente_paiement, stats.total)}
        />

        {/* En attente de livraison (Rouge comme demandé) */}
        <StatCard
          title="Attente Livraison"
          value={stats.attente_livraison}
          icon="📦"
          color="border-red-500 text-red-600"
          bgColor="bg-gradient-to-br from-red-50 to-red-100"
          percentage={calculatePercentage(stats.attente_livraison, stats.total)}
        />

        {/* Livrées */}
        <StatCard
          title="Livrées"
          value={stats.livre}
          icon="✅"
          color="border-green-500 text-green-600"
          bgColor="bg-gradient-to-br from-green-50 to-green-100"
          percentage={calculatePercentage(stats.livre, stats.total)}
        />

        {/* Clôturées */}
        <StatCard
          title="Clôturées"
          value={stats.cloture}
          icon="🏁"
          color="border-gray-500 text-gray-600"
          bgColor="bg-gradient-to-br from-gray-50 to-gray-100"
          percentage={calculatePercentage(stats.cloture, stats.total)}
        />

        {/* Supprimées */}
        <StatCard
          title="Supprimées"
          value={stats.supprimee || 0}
          icon="🗑️"
          color="border-orange-500 text-orange-600"
          bgColor="bg-gradient-to-br from-orange-50 to-orange-100"
          percentage={calculatePercentage(stats.supprimee || 0, stats.total)}
        />
      </div>

      {/* Indicateur de performance */}
      <div className="mt-4 sm:mt-6 bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-200 rounded-xl p-4 overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-blue-900 truncate">Taux de traitement</p>
              <p className="text-xs text-blue-700 truncate">
                {calculatePercentage(stats.livre + stats.cloture, stats.total)}% des souscriptions traitées
              </p>
            </div>
          </div>

          <div className="text-left sm:text-right flex-shrink-0">
            <p className="text-sm font-bold text-orange-600 whitespace-nowrap">
              {stats.attente_paiement + stats.attente_livraison} en cours
            </p>
            <p className="text-xs text-orange-500 whitespace-nowrap">Nécessitent une action</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatistiquesSection;